import type { Monaco } from "@monaco-editor/react";

/** Reads a CSS custom property (e.g. '--card') from :root and converts the
 *  bare HSL value ("222 18% 15%") to a hex string. Returns '' on failure. */
function cssVarToHex(varName: string): string {
  try {
    const hslParts = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    if (!hslParts) return "";
    const el = document.createElement("div");
    el.style.display = "none";
    el.style.color = `hsl(${hslParts})`;
    document.body.appendChild(el);
    const rgb = getComputedStyle(el).color;
    document.body.removeChild(el);
    const nums = (rgb.match(/\d+/g) ?? []).map(Number);
    if (nums.length < 3) return "";
    return `#${nums[0].toString(16).padStart(2, "0")}${nums[1].toString(16).padStart(2, "0")}${nums[2].toString(16).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

/**
 * Defines `ncoder-dark` and `ncoder-light` Monaco themes that inherit from
 * `vs-dark`/`vs` but use the app's `--card` design token for the editor
 * background, so code blocks visually integrate with the current theme.
 *
 * Call from a Monaco editor's `beforeMount` callback, and re-call via
 * `useEffect` on `resolvedTheme` changes so the definition stays current
 * after the editor is already mounted.
 */
export function registerAppMonacoThemes(monaco: Monaco, isDark: boolean): void {
  // Read --card only when in dark mode; the computed value matches the active
  // theme. In light mode the card is always white, so hardcode #ffffff.
  const darkBg = isDark ? cssVarToHex("--card") || "#1f232d" : "#1f232d";

  monaco.editor.defineTheme("ncoder-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [],
    colors: { "editor.background": darkBg },
  });

  monaco.editor.defineTheme("ncoder-light", {
    base: "vs",
    inherit: true,
    rules: [],
    colors: { "editor.background": "#ffffff" },
  });
}
